import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { InitState } from '../../../../../../redux/reducers/root/initialState'
import { ContentStateProperty, OnChangeValue } from '../../../../../../@types/types'
import ToggleSwitch from '../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import { LocationInput } from '../../../../../common/inputs/locationInput/LocationInput'
import ManualGeoCoding from '../../../../../common/inputs/manualGeoCoding/ManualGeoCoding'
import { AddressPageProps } from '../../../../../../@types/props-types'
import { SCREEN_ADDRESS, SCREENS } from '../../../../../../services/domain/wizardScreens'
import { AddressScreenValidationResult } from '../../../../../../@types/validation-types'
import LocationMap from '../../../../../common/inputs/locationMap/LocationMap'
import { TextInput } from '../../../../../common/inputs/textInput/TextInput'
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks'
import { queryGeoCodesDebounced } from '../../../../../../redux/thunks/gisService/gisService'
import Checkbox from '../../../../../common/inputs/checkbox/Checkbox'
import { StreetField } from '../streetField/StreetField'
import { isWebView } from '../../../../../../utils/navigation'

const screen = SCREENS[SCREEN_ADDRESS]

const NationalAddress: React.FC<AddressPageProps<AddressScreenValidationResult>> = ({
  insertionData,
  validationMessages,
  onChange: onChangeFromPros,
  doReset,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { locations, locationType, hasValidGeoCoordinates, geoCoordinates } = useAppSelector(
    (state: InitState) => state.addressFeature
  )
  const prevHasValidGeoCoordinatesRef = useRef(hasValidGeoCoordinates)
  const isWebViewEnabled = isWebView()
  useEffect(() => {
    // Only reset when transitioning from invalid to valid
    if (hasValidGeoCoordinates && !prevHasValidGeoCoordinatesRef.current && doReset) {
      doReset([
        {
          screen: screen.name,
          property: 'manualRegion',
        },
        {
          screen: screen.name,
          property: 'manualCity',
        },
        {
          screen: screen.name,
          property: 'manualQuarter',
        },
      ])
    }

    // Update ref with current value for next comparison
    prevHasValidGeoCoordinatesRef.current = hasValidGeoCoordinates
  }, [hasValidGeoCoordinates, doReset])
  const areGeoCoordinatesSet =
    !!(geoCoordinates.longitude !== 0 && geoCoordinates.latitude) || !!insertionData.manualQuarter?.geoCoordinates

  const onChange = useCallback(
    (field: ContentStateProperty, value: OnChangeValue) => {
      onChangeFromPros({ screen: screen.name, property: field }, value)
      dispatch(queryGeoCodesDebounced())
    },
    [dispatch, onChangeFromPros]
  )

  return (
    <>
      <div className={`grid-item ${isWebViewEnabled ? 'palm-one-whole' : 'nine-twelfths'}`}>
        <StreetField
          insertionData={insertionData}
          validationMessages={validationMessages}
          locations={locations}
          locationType={locationType}
        />
      </div>
      <div className={`grid-item ${isWebViewEnabled ? 'palm-one-whole margin-top-l' : 'two-twelfth push-one-twelfth'}`}>
        <TextInput
          id='house-number'
          propertyName='houseNumber'
          placeholder=''
          label={
            <>
              {isWebViewEnabled
                ? t('wizard.screen.nationalAddress.mobile.houseNumber', 'Hausnummer*')
                : t('wizard.screen.nationalAddress.houseNumber', 'Nr*')}
            </>
          }
          value={insertionData.houseNumber}
          type='text'
          errors={validationMessages!.houseNumber ?? []}
          doOnFieldChange={onChange}
          className={'pii-hide'}
          dataHeyimmoContext='address_house_number'
        />
      </div>
      {!isWebViewEnabled && (
        <div className='grid-item one-whole margin-top-s'>
          <ToggleSwitch
            id='address-visibility'
            alignment='right'
            propertyName='hideStreetAndStreetNumber'
            label={<span>{t('wizard.screen.address.visibility', 'Visible to interested people')}</span>}
            value={insertionData.hideStreetAndStreetNumber ?? false}
            onChange={onChange}
          />
        </div>
      )}
      <div className={`grid-item margin-top-l ${isWebViewEnabled ? 'palm-one-whole' : 'three-twelfths'}`}>
        <LocationInput
          id='postcode'
          propertyName='postcode'
          label={
            isWebViewEnabled
              ? t('wizard.screen.nationalAddress.mobile.postcode', 'Postleitzahl*')
              : t('wizard.screen.nationalAddress.postcode', 'Postleitzahl*')
          }
          input={insertionData.postcode}
          type='tel' // 'number' type does not trigger the onChange in safari - using 'tel' is the usual workaround!
          style={{}}
          errors={validationMessages!.postcode ?? []}
          locations={locations}
          locationType={locationType}
          maxLength={5}
          onChange={onChange}
          dataHeyimmoContext='address_postcode'
        />
      </div>
      <div
        className={`grid-item margin-top-l ${isWebViewEnabled ? 'palm-one-whole' : 'eight-twelfths push-one-twelfth'}`}
      >
        <LocationInput
          id='city'
          propertyName='city'
          maxLength={100}
          label='wizard.screen.address.city'
          input={insertionData.city}
          type='text'
          style={{}}
          errors={validationMessages!.city ?? []}
          locations={locations}
          locationType={locationType}
          onChange={onChange}
          dataHeyimmoContext='address_city'
        />
      </div>
      {isWebViewEnabled && (
        <div className='grid-item one-whole margin-top-s'>
          <Checkbox
            id='show-whole-address'
            onChange={(_name, checked) =>
              onChangeFromPros({ screen: screen.name, property: 'showWholeAddress' }, checked)
            }
            name='showWholeAddress'
            checked={insertionData.showWholeAddress ?? false}
            label={<span>{t('wizard.screen.address.showWholeAddress', 'unknown')}</span>}
          />
        </div>
      )}
      {!hasValidGeoCoordinates && (
        <div className='grid-item margin-top-xxl one-whole'>
          <ManualGeoCoding
            insertionData={insertionData}
            onChange={onChange}
            validationMessages={validationMessages}
            doReset={doReset}
          />
        </div>
      )}
      {areGeoCoordinatesSet && (
        <div className='grid-item margin-top-l visible-google-map'>
          <LocationMap geoCoordinates={geoCoordinates} insertionData={insertionData} />
        </div>
      )}
    </>
  )
}

export default NationalAddress
